import React from "react";
import { Tooltip, TooltipProps } from "@chakra-ui/react";

/**
 * SafeTooltip — a Chakra Tooltip that CANNOT stay on screen (2026-09-25, Tom: "the hover texts are showing
 * indefinitely"). Every tooltip in this module goes through it — via the kit's `GovTip`,
 * or `import { SafeTooltip as Tooltip }` — never Chakra's `Tooltip` directly.
 *
 * Why Chakra's own tooltip sticks: it closes on the trigger's pointer-LEAVE. When the trigger is re-rendered
 * or unmounted while its tooltip is open (the calendar grid redraws event chips on every poll; the inbox list
 * refreshes live; a Menu or Modal opens over the button; a disabled button fires no pointer events), that
 * leave never arrives and the tooltip stays forever. Chakra also defaults `closeOnScroll` to off.
 *
 * What this adds, in order of how often it saves you:
 *  1. A watchdog while open: every 200ms it checks that the trigger is still in the page AND is either under
 *     the pointer (`:hover`) or keyboard-focused (`:focus-visible`). Anything else closes it — so a redraw,
 *     an unmount, a dialog on top, or a mouse click that left focus behind all end the tooltip.
 *  2. Close on scroll (any scroller), wheel, pointer-down anywhere, Escape, window blur, tab hidden.
 *  3. `closeDelay={0}`, `openDelay={300}` so pointer transit never flashes one open.
 * A trigger that disappears closes it at once; one the watchdog could never mark (a component that drops
 * unknown props) is closed after 8 seconds.
 * A caller-controlled tooltip (`isOpen` passed in) is left exactly as the caller drives it.
 */
let nextId = 0;
const WATCH_MS = 200;
const UNMARKED_MAX_MS = 8000;

export const SafeTooltip: React.FC<TooltipProps> = ({ children, isOpen: controlled, onOpen, onClose, shouldWrapChildren, ...rest }) => {
  const id = React.useMemo(() => `tip${++nextId}`, []);
  const [open, setOpen] = React.useState(false);
  const isControlled = controlled !== undefined;

  React.useEffect(() => {
    if (!open || isControlled) return;
    const openedAt = Date.now();
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    const onVisibility = () => { if (document.hidden) close(); };
    let seenMarked = false;
    const timer = window.setInterval(() => {
      const marked = document.querySelectorAll(`[data-safe-tip="${id}"]`);
      if (marked.length === 0) {
        // Seen and now gone = the trigger was removed or redrawn: close at once. Never seen = a component that
        // drops unknown props; fall back to a time limit.
        if (seenMarked || Date.now() - openedAt > UNMARKED_MAX_MS) close();
        return;
      }
      seenMarked = true;
      const alive = Array.from(marked).some((el) => el.isConnected && (el.matches(":hover") || el.matches(":focus-visible") || !!el.querySelector(":focus-visible")));
      if (!alive) close();
    }, WATCH_MS);
    window.addEventListener("scroll", close, true);
    window.addEventListener("wheel", close, { passive: true });
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("blur", close);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("wheel", close);
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", close);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [open, isControlled, id]);

  // Mark the trigger so the watchdog can find it. A plain single element is marked in place (no layout change);
  // text, fragments and `shouldWrapChildren` get the same inline-block span Chakra itself would have used.
  const single = React.isValidElement(children) && children.type !== React.Fragment;
  const trigger = !shouldWrapChildren && single
    ? React.cloneElement(children as React.ReactElement<any>, { "data-safe-tip": id })
    : <span data-safe-tip={id} tabIndex={0} style={{ display: "inline-block" }}>{children}</span>;

  return (
    <Tooltip
      hasArrow
      openDelay={300}
      closeDelay={0}
      placement="top"
      closeOnScroll
      closeOnEsc
      closeOnPointerDown
      {...rest}
      isOpen={isControlled ? controlled : open}
      onOpen={() => { if (!isControlled) setOpen(true); onOpen?.(); }}
      onClose={() => { if (!isControlled) setOpen(false); onClose?.(); }}
    >
      {trigger}
    </Tooltip>
  );
};

export default SafeTooltip;
